import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import type { AdminOrder, AdminOrderStatus } from '../mocks/adminOrders';

const ORDERS_KEY = 'sipdash_orders';
const MOCK_HIDDEN_KEY = 'sipdash_mock_orders_hidden';

function loadFromStorage(): AdminOrder[] {
  try {
    const raw = localStorage.getItem(ORDERS_KEY);
    return raw ? (JSON.parse(raw) as AdminOrder[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(orders: AdminOrder[]): void {
  try {
    localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
    // Notify other tabs
    window.dispatchEvent(new Event('sipdash_order_saved'));
  } catch {
    // ignore quota errors
  }
}

export function generateOrderId(): string {
  return `SDG-${Math.floor(10000 + Math.random() * 90000)}`;
}

interface OrdersContextValue {
  orders: AdminOrder[];
  pendingPayments: AdminOrder[];
  confirmedOrders: AdminOrder[];
  mockHidden: boolean;
  addOrder: (order: AdminOrder) => void;
  updateStatus: (id: string, status: AdminOrderStatus) => void;
  confirmWithRider: (id: string, rider: string) => void;
  cancelOrder: (id: string) => boolean;
  clearAllOrders: () => void;
  getOrdersByUser: (userId: string) => AdminOrder[];
}

const OrdersContext = createContext<OrdersContextValue | null>(null);

export function OrdersProvider({ children }: { children: ReactNode }) {
  const [orders, setOrders] = useState<AdminOrder[]>(() => loadFromStorage());
  const [mockHidden, setMockHidden] = useState<boolean>(
    () => localStorage.getItem(MOCK_HIDDEN_KEY) === '1'
  );

  // Sync to localStorage whenever orders change
  useEffect(() => {
    saveToStorage(orders);
  }, [orders]);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === ORDERS_KEY) {
        setOrders(loadFromStorage());
      }
      if (e.key === MOCK_HIDDEN_KEY) {
        setMockHidden(localStorage.getItem(MOCK_HIDDEN_KEY) === '1');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const addOrder = useCallback((order: AdminOrder) => {
    setOrders((prev) => {
      const idx = prev.findIndex((o) => o.id === order.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = order;
        return updated;
      }
      return [order, ...prev];
    });
  }, []);

  const updateStatus = useCallback((id: string, status: AdminOrderStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, status } : o))
    );
  }, []);

  const confirmWithRider = useCallback((id: string, rider: string) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === id ? { ...o, status: 'pending' as AdminOrderStatus, rider } : o
      )
    );
  }, []);

  const cancelOrder = useCallback((id: string): boolean => {
    let success = false;
    setOrders((prev) => {
      const order = prev.find((o) => o.id === id);
      if (!order || order.status !== 'payment_pending') return prev;
      success = true;
      return prev.map((o) =>
        o.id === id ? { ...o, status: 'cancelled' as AdminOrderStatus } : o
      );
    });
    return success;
  }, []);

  const clearAllOrders = useCallback(() => {
    setOrders([]);
    setMockHidden(true);
    localStorage.removeItem(ORDERS_KEY);
    localStorage.setItem(MOCK_HIDDEN_KEY, '1');
  }, []);

  const getOrdersByUser = useCallback(
    (userId: string) => orders.filter((o) => o.userId === userId),
    [orders]
  );

  const pendingPayments = useMemo(
    () => orders.filter((o) => o.status === 'payment_pending'),
    [orders]
  );

  const confirmedOrders = useMemo(
    () => orders.filter((o) => o.status !== 'payment_pending'),
    [orders]
  );

  return (
    <OrdersContext.Provider
      value={{
        orders,
        pendingPayments,
        confirmedOrders,
        mockHidden,
        addOrder,
        updateStatus,
        confirmWithRider,
        cancelOrder,
        clearAllOrders,
        getOrdersByUser,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used inside OrdersProvider');
  return ctx;
}
